/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { AgentResourceReference } from './types';

export const agentResourceHref = (reference: AgentResourceReference): string | undefined => {
  switch (reference.resource_type) {
    case 'pvsim_config':
      return configHref('/pv/pvsim/simulate', reference);
    case 'pvcosting_config':
      return configHref('/pv/costing/simulate', reference);
    case 'esscosting_config':
      return configHref('/ess/costing/simulate', reference);
    case 'plantsim_config':
      return configHref('/plant-v2/simulate', reference);
    case 'pvsim_run':
      return `/pv/pvsim/results?scenario=${encodeURIComponent(reference.resource_id)}`;
    case 'weather':
      return `/db/weather/${encodeURIComponent(reference.resource_id)}`;
    case 'met_station':
      return `/db/met-stations/${encodeURIComponent(reference.resource_id)}`;
    case 'pv_module':
      return `/db/pvmodules/${encodeURIComponent(reference.resource_id)}`;
    case 'pv_inverter':
      return `/db/pvinverters/${encodeURIComponent(reference.resource_id)}`;
    case 'battery':
      return `/db/batteries/${encodeURIComponent(reference.resource_id)}`;
    case 'ess_inverter':
      return `/db/essinverters/${encodeURIComponent(reference.resource_id)}`;
    case 'mv_transformer':
      return `/db/mv-transformers/${encodeURIComponent(reference.resource_id)}`;
    case 'project':
      return `/projects/${encodeURIComponent(reference.resource_id)}/`;
    default:
      return undefined;
  }
};

const configHref = (basePath: string, reference: AgentResourceReference) => {
  const params = new URLSearchParams({ config: reference.resource_id });
  if (typeof reference.version === 'number') {
    params.set('version', String(reference.version));
  }
  return `${basePath}?${params.toString()}`;
};

export const linkAgentResourceIdsInMarkdown = (
  text: string,
  references: Array<AgentResourceReference> | undefined,
): string => {
  if (!text || !references?.length) return text;

  const linkableReferences = dedupeAgentResourceReferences(references)
    .flatMap((reference) => {
      const href = agentResourceHref(reference);
      return href ? [{ href, reference }] : [];
    })
    .sort((left, right) => right.reference.resource_id.length - left.reference.resource_id.length);

  if (!linkableReferences.length) return text;

  const textWithLinkedCodeSpans = linkMarkdownResourceCodeSpans(text, linkableReferences);
  return transformMarkdownTextSegments(textWithLinkedCodeSpans, (segment) =>
    linkResourceIdsInText(segment, linkableReferences),
  );
};

export const normalizeAgentResourceReferences = (value: unknown): Array<AgentResourceReference> => {
  if (!Array.isArray(value)) return [];

  return dedupeAgentResourceReferences(
    value.flatMap((entry) => {
      const reference = normalizeAgentResourceReference(entry);
      return reference && agentResourceHref(reference) ? [reference] : [];
    }),
  );
};

export const dedupeAgentResourceReferences = (
  references: Array<AgentResourceReference | undefined>,
): Array<AgentResourceReference> => {
  const byKey = new Map<string, AgentResourceReference>();
  for (const reference of references) {
    if (!reference) continue;
    const key = `${reference.resource_type}:${reference.resource_id}:${reference.version ?? ''}`;
    if (!byKey.has(key)) {
      byKey.set(key, reference);
    }
  }
  return Array.from(byKey.values());
};

const normalizeAgentResourceReference = (value: unknown): AgentResourceReference | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const resourceType = stringValue(record.resource_type);
  const resourceId = stringValue(record.resource_id);
  if (!resourceType || !resourceId) return undefined;

  const version = numberValue(record.version);
  return {
    resource_type: resourceType,
    resource_id: resourceId,
    version,
    label: stringValue(record.label) ?? fallbackResourceLabel(resourceType, resourceId, version),
  };
};

const RESOURCE_LABEL_PREFIXES: Record<string, string> = {
  pvsim_config: 'PVSim config',
  pvcosting_config: 'PVCosting config',
  esscosting_config: 'ESSCosting config',
  plantsim_config: 'PlantSim config',
  pvsim_run: 'PVSim run',
  weather: 'Weather',
  met_station: 'Met station',
  pv_module: 'PV module',
  pv_inverter: 'PV inverter',
  battery: 'Battery',
  ess_inverter: 'ESS inverter',
  mv_transformer: 'MV transformer',
  project: 'Project',
};

const fallbackResourceLabel = (
  resourceType: string,
  resourceId: string,
  version: number | null,
) => {
  const prefix = RESOURCE_LABEL_PREFIXES[resourceType];
  if (!prefix) return resourceId;
  const label = `${prefix} ${resourceId}`;
  return version === null ? label : `${label} v${version}`;
};

const stringValue = (value: unknown) => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const numberValue = (value: unknown) => {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return null;
};

const linkResourceIdsInText = (
  text: string,
  linkableReferences: Array<{ href: string; reference: AgentResourceReference }>,
) => {
  let output = '';
  let index = 0;

  while (index < text.length) {
    const match = linkableReferences.find(({ reference }) =>
      matchesResourceIdAt(text, reference.resource_id, index),
    );
    if (!match) {
      output += text[index];
      index += 1;
      continue;
    }

    output += markdownResourceLink(match);
    index += match.reference.resource_id.length;
  }

  return output;
};

const linkMarkdownResourceCodeSpans = (
  text: string,
  linkableReferences: Array<{ href: string; reference: AgentResourceReference }>,
) =>
  text.replace(/`([^`\n]+)`/g, (codeSpan, codeContent: string) => {
    const match = linkableReferences.find(
      ({ reference }) => reference.resource_id === codeContent.trim(),
    );
    return match ? markdownResourceLink(match) : codeSpan;
  });

const matchesResourceIdAt = (text: string, resourceId: string, index: number) => {
  if (!text.startsWith(resourceId, index)) return false;

  return (
    !isResourceIdBoundaryChar(text[index - 1]) &&
    !isResourceIdBoundaryChar(text[index + resourceId.length])
  );
};

const isResourceIdBoundaryChar = (value: string | undefined) =>
  typeof value === 'string' && /[A-Za-z0-9_-]/.test(value);

const transformMarkdownTextSegments = (
  text: string,
  transformTextSegment: (segment: string) => string,
) => {
  let output = '';
  let index = 0;

  while (index < text.length) {
    const protectedSegmentEndIndex = markdownProtectedSegmentEndIndex(text, index);
    if (protectedSegmentEndIndex !== null) {
      output += text.slice(index, protectedSegmentEndIndex);
      index = protectedSegmentEndIndex;
      continue;
    }

    const nextProtectedSegmentStartIndex = nextMarkdownProtectedSegmentStartIndex(text, index + 1);
    const textSegmentEndIndex = nextProtectedSegmentStartIndex ?? text.length;
    output += transformTextSegment(text.slice(index, textSegmentEndIndex));
    index = textSegmentEndIndex;
  }

  return output;
};

const markdownProtectedSegmentEndIndex = (text: string, index: number) => {
  const codeSpanEndIndex = markdownCodeSpanEndIndex(text, index);
  if (codeSpanEndIndex !== null) return codeSpanEndIndex;

  return markdownLinkEndIndex(text, index);
};

const nextMarkdownProtectedSegmentStartIndex = (text: string, startIndex: number) => {
  const codeStartIndex = text.indexOf('`', startIndex);
  const linkStartIndex = text.indexOf('[', startIndex);
  if (codeStartIndex === -1) return linkStartIndex === -1 ? null : linkStartIndex;
  if (linkStartIndex === -1) return codeStartIndex;
  return Math.min(codeStartIndex, linkStartIndex);
};

const markdownCodeSpanEndIndex = (text: string, index: number) => {
  if (text[index] !== '`') return null;

  const openingTicks = /^`+/.exec(text.slice(index))?.[0];
  if (!openingTicks) return null;

  const closingIndex = text.indexOf(openingTicks, index + openingTicks.length);
  if (closingIndex === -1) return null;

  return closingIndex + openingTicks.length;
};

const markdownLinkEndIndex = (text: string, index: number) => {
  if (text[index] !== '[') return null;

  const closeLabelIndex = text.indexOf(']', index + 1);
  if (closeLabelIndex === -1 || text[closeLabelIndex + 1] !== '(') return null;

  const closeHrefIndex = text.indexOf(')', closeLabelIndex + 2);
  return closeHrefIndex === -1 ? null : closeHrefIndex + 1;
};

const markdownResourceLink = ({
  href,
  reference,
}: {
  href: string;
  reference: AgentResourceReference;
}) => `[${escapeMarkdownLinkText(reference.label)}](${href})`;

const escapeMarkdownLinkText = (value: string) => value.replace(/[\\[\]]/g, '\\$&');
